/**
 * <cart-upsell-recommendations>
 *
 * Fetches Search & Discovery recommendations for the products currently in the
 * cart and injects them as cards. Because the endpoint takes a single seed
 * product, the configured total limit is distributed across the cart's unique
 * products, and each seed is fetched via the Section Rendering API
 * (section: upsell-recommendations), which returns ready-rendered card slides.
 *
 * Correctness comes from reacting to the Alpine cart STORE (via Alpine.effect),
 * not DOM events: any change to the cart's product set — add, remove, or an
 * empty→filled transition — reruns the effect and rebuilds the list off the
 * live cart, always excluding whatever is currently in the cart. (Quantity-only
 * changes don't alter the product set, so they don't trigger a refetch.)
 *
 * Loading is lazy: the first fetch waits until the element is visible.
 *
 * Design: vertical compact list on desktop (sidebar), swipeable carousel on
 * mobile. Swiper is only instantiated at mobile widths and destroyed on
 * desktop, so the same markup serves both.
 */
if (!customElements.get('cart-upsell-recommendations')) {
  class CartUpsellRecommendations extends HTMLElement {
    constructor() {
      super();
      this.intent = this.dataset.intent || 'related';
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.sectionId = 'upsell-recommendations';
      this.mq = window.matchMedia('(min-width: 768px)');
      this.swiper = null;

      this.visible = false;
      this.buildToken = 0;
      this.builtSig = null;
      this.nextSig = null;
      this.nextIds = [];
      this._buildTimer = null;

      try {
        this.fallbackIds = JSON.parse(this.dataset.productIds) || [];
      } catch (_) {
        this.fallbackIds = [];
      }

      this.wrapper = this.querySelector('.swiper-wrapper');
      this.heading = this.querySelector('.cart-upsell__heading');
      this.carouselWrap = this.querySelector('.cart-upsell__carousel-wrap');
      this._syncSwiper = this.syncSwiper.bind(this);
    }

    connectedCallback() {
      if (!this.limit || !this.wrapper) return;

      // Lazy: only fetch once the element has been visible at least once.
      this.io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        this.io.disconnect();
        this.visible = true;
        this.maybeBuild();
      });
      this.io.observe(this);

      this.startReactivity();
      this.mq.addEventListener('change', this._syncSwiper);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.mq.removeEventListener('change', this._syncSwiper);
      clearTimeout(this._buildTimer);
      if (this._effect && window.Alpine && typeof window.Alpine.release === 'function') {
        window.Alpine.release(this._effect);
      }
      if (this._onCartEvent) document.removeEventListener('cart-update', this._onCartEvent);
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    // Live cart product ids from the store; falls back to the server-rendered
    // value until the store has loaded.
    currentCartProductIds() {
      const store = window.Alpine && window.Alpine.store('cart');
      if (store && store.isLoaded && Array.isArray(store.items)) {
        return store.items.map((i) => i.product_id).filter(Boolean);
      }
      return this.fallbackIds;
    }

    startReactivity() {
      const run = () => {
        const ids = this.currentCartProductIds();
        this.nextIds = ids;
        this.nextSig = [...new Set(ids.map(String))].sort().join(',');
        this.maybeBuild();
      };

      if (window.Alpine && typeof window.Alpine.effect === 'function') {
        // Reruns whenever the tracked store fields (isLoaded / items) change.
        this._effect = window.Alpine.effect(run);
      } else {
        // Fallback if Alpine.effect is unavailable.
        this._onCartEvent = run;
        document.addEventListener('cart-update', this._onCartEvent);
        run();
      }
    }

    maybeBuild() {
      if (!this.visible) return;
      if (this.nextSig === this.builtSig) return;
      clearTimeout(this._buildTimer);
      this._buildTimer = setTimeout(() => this.build(), 150);
    }

    async build() {
      const ids = this.nextIds;
      this.builtSig = this.nextSig;
      const token = ++this.buildToken;

      // Reset current content.
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.wrapper.innerHTML = '';
      this.carouselWrap?.classList.add('hidden');
      this.heading?.classList.add('hidden');

      const seeds = [...new Set(ids)];
      if (!seeds.length) return;

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const base = Math.floor(this.limit / seeds.length);
        const extra = this.limit % seeds.length;
        const seedLimits = seeds
          .map((id, i) => ({ id, limit: base + (i < extra ? 1 : 0) }))
          .filter((p) => p.limit > 0);

        const responses = await Promise.all(
          seedLimits.map((p) =>
            fetch(
              `${root}recommendations/products?product_id=${p.id}&limit=${p.limit}` +
                `&intent=${this.intent}&section_id=${this.sectionId}`
            )
          )
        );
        if (token !== this.buildToken) return; // superseded by a newer build

        // Exclude products already in the cart, and de-duplicate across seeds.
        const exclude = new Set(ids.map(String));
        const fragment = document.createDocumentFragment();

        for (let i = 0; i < responses.length; i++) {
          if (!responses[i].ok) continue;
          const text = await responses[i].text();
          if (token !== this.buildToken) return;

          const holder = document.createElement('div');
          holder.innerHTML = text;

          const cards = holder.querySelectorAll('[data-upsell-card]');
          let added = 0;
          for (const card of cards) {
            if (added >= seedLimits[i].limit) break;
            const pid = card.getAttribute('data-product-id');
            if (pid && exclude.has(pid)) continue;
            if (pid) exclude.add(pid);
            fragment.appendChild(card);
            added++;
          }
        }
        if (token !== this.buildToken) return;

        this.wrapper.appendChild(fragment);

        if (this.wrapper.querySelector('[data-upsell-card]')) {
          this.carouselWrap?.classList.remove('hidden');
          this.heading?.classList.remove('hidden');
          this.syncSwiper();
        }
      } catch (err) {
        console.error('Cart upsell recommendations failed:', err);
      }
    }

    // Carousel on mobile; destroy Swiper on desktop so cards flow as a list.
    syncSwiper() {
      if (this.mq.matches) {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
        return;
      }

      if (this.swiper) return;

      const el = this.querySelector('.swiper-cart-upsell');
      if (!el || !window.Swiper) return;

      const modules = window.SwiperModules ? [window.SwiperModules.Pagination] : [];
      this.swiper = new window.Swiper(el, {
        modules,
        slidesPerView: 2.2,
        spaceBetween: 12,
        grabCursor: true,
        watchOverflow: true,
        pagination: {
          el: this.querySelector('.cart-upsell__pagination'),
          clickable: true,
        },
      });
    }
  }

  customElements.define('cart-upsell-recommendations', CartUpsellRecommendations);
}
